import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Descriptions, Button, Space, Tag, Spin, Alert, Modal, message, Table } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined, PlusOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { getCustomer, deleteCustomer, type Customer } from '@/api/customerApi';
import { listContacts, type Contact } from '@/api/contactApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  prospect: 'blue',
  lead: 'orange',
  churned: 'red',
};

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const canWrite = hasPermission('customers', 'write');
  const canDelete = hasPermission('customers', 'delete');
  const canWriteContacts = hasPermission('contacts', 'write');

  const loadContacts = useCallback((customerId: string) => {
    setContactsLoading(true);
    listContacts({ from: 0, size: 200 }, { customer_id: customerId })
      .then((res) => setContacts(res.items))
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getCustomer(id)
      .then((c) => {
        setCustomer(c);
        loadContacts(id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t, loadContacts]);

  const contactColumns: ColumnsType<Contact> = useMemo(
    () => [
      {
        title: t('contacts.field.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record: Contact) => (
          <Link to={`/contacts/${record.id}`}>{name}</Link>
        ),
      },
      {
        title: t('contacts.field.email'),
        dataIndex: 'email',
        key: 'email',
        ellipsis: true,
      },
      {
        title: t('contacts.field.phone'),
        dataIndex: 'phone',
        key: 'phone',
      },
      {
        title: t('contacts.field.title'),
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
      },
      {
        title: t('contacts.field.is_primary'),
        dataIndex: 'is_primary',
        key: 'is_primary',
        width: 100,
        render: (val: boolean) =>
          val ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null,
      },
    ],
    [t]
  );

  const handleDelete = () => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('customers.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteCustomer(id);
          message.success(t('customers.delete_success'));
          navigate('/customers');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (error || !customer) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          {t('app.back')}
        </Button>
        <Alert type="error" message={error || t('app.not_found')} style={{ marginTop: 16 }} />
      </div>
    );
  }

  const formatDate = (val: string) => val ? new Date(val).toLocaleString() : '-';

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/customers/${id}/edit`)}
          >
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>{t('customers.detail_title')}</h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('customers.field.name')}>{customer.name}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.status')}>
          <Tag color={STATUS_COLORS[customer.status] ?? 'default'}>
            {t(`customers.status_options.${customer.status}`, customer.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.email')}>{customer.email || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.phone')}>{customer.phone || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.company')}>{customer.company || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.industry')}>{customer.industry || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.address')} span={2}>{customer.address || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.website')}>
          {customer.website ? (
            <a href={customer.website} target="_blank" rel="noopener noreferrer">{customer.website}</a>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.source')}>{customer.source || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.assigned_to')}>{customer.assigned_to || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.branch_id')}>{customer.branch_id || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.tags')}>
          {(customer.tags?.length ?? 0) > 0
            ? customer.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.notes')} span={2}>
          {customer.notes || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.created_by')}>{customer.created_by || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.created_at')}>{formatDate(customer.created_at)}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.updated_by')}>{customer.updated_by || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('customers.field.updated_at')}>{formatDate(customer.updated_at)}</Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 32 }}>
        <Space style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{t('contacts.title')}</h3>
          {canWriteContacts && (
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/contacts/new?customer_id=${id}`)}
            >
              {t('contacts.new_contact')}
            </Button>
          )}
        </Space>
        <Table<Contact>
          columns={contactColumns}
          dataSource={contacts}
          rowKey="id"
          loading={contactsLoading}
          pagination={false}
          size="small"
          locale={{ emptyText: t('contacts.no_contacts') }}
          onRow={(record) => ({
            onDoubleClick: () => navigate(`/contacts/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </div>
  );
}
